import type { ReactNode } from "react";
import { SlideHeader } from "../../../SlideHeader";

// TODO(thomas/yevhen): replace with the public sample-audit URL before the
// deck is shared. Left as "#" so the placeholder is obvious in-app.
const EXAMPLE_AUDIT_URL = "#";

interface QA {
  q: string;
  a: ReactNode;
}

const QUESTIONS: QA[] = [
  {
    q: "You promise more organic installs. How do you prove the platform moves that number?",
    a: (
      <div className="space-y-1.5">
        <Tier label="Promise">
          the objective — <span className="text-slate-900">organic install growth</span>.
        </Tier>
        <Tier label="Prove">
          the mechanism, today — conversion-rate and visibility deltas on the
          customer's own App Store Connect and Play Console data, tied via{" "}
          <span className="text-slate-900">Score History</span> to specific
          published changes. They see which change moved which delta, on their
          own numbers.
        </Tier>
        <Tier label="Concede">
          the gold standard — <em>causal</em> install attribution ("installs
          would have grown by X without us") is what a paid pilot or geo-holdout
          answers. We never claim it from the dashboard.
        </Tier>
        <p className="pt-0.5 text-slate-500">
          That discipline is what separates us from tools that promise to
          "skyrocket installs" and prove nothing.
        </p>
      </div>
    ),
  },
  {
    q: "Don't paid attribution tools — AppsFlyer, Adjust, Branch — already do this?",
    a: (
      <>
        Different surface, different question. They attribute installs to{" "}
        <span className="text-slate-900">ad campaigns</span> (paid acquisition).
        Score History attributes published listing changes to{" "}
        <span className="text-slate-900">
          conversion and visibility deltas on the store
        </span>{" "}
        (organic). They answer "which campaign drove the install?"; we answer
        "which listing change moved the conversion rate and the impressions?"
        The two coexist.
      </>
    ),
  },
  {
    q: "If AI coding lowered the barrier to build here, what stops others from competing?",
    a: (
      <>
        Standing up a demo is easy now; operating a production-grade, integrated
        ASO platform <span className="text-slate-900">every day</span> is not. It
        takes a rare pairing — senior product-and-engineering depth and deep ASO
        domain mastery — plus the conviction to run it day in, day out.
        Incumbents won't rebuild on legacy stacks; new entrants ship thin model
        wrappers, not the system.
      </>
    ),
  },
  {
    q: "Won't a frontier lab — OpenAI, Anthropic, Google — just launch this?",
    a: (
      <>
        The math doesn't work for them. ASO is{" "}
        <span className="text-slate-900">venture-scale for a focused startup</span>,
        yet immaterial on a frontier lab's P&amp;L even at full share. Their game
        is selling infrastructure and ad-supported reach at massive scale — a
        vertical ASO platform isn't their Everest. They're likelier to be the
        model layer beneath us than a competitor.
      </>
    ),
  },
  {
    q: "What's the corporate and ownership structure?",
    a: (
      <>
        A <span className="text-slate-900">Delaware C-Corp</span>, incorporated
        via Stripe Atlas on standard Cooley documents — no unorthodox structures
        or clauses. Thomas holds ~50%, Yevhen ~35%, with the remainder in an
        employee and advisor option pool.
      </>
    ),
  },
  {
    q: "Can I see an example audit report?",
    a: (
      <>
        Yes —{" "}
        <a
          href={EXAMPLE_AUDIT_URL}
          target="_blank"
          rel="noreferrer"
          className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-2 hover:decoration-blue-700"
        >
          view a full sample audit
        </a>{" "}
        generated by Apptonomy on a live store listing.
      </>
    ),
  },
];

export default function CommonQuestions() {
  return (
    <div className="flex w-full flex-col px-6 pt-8 pb-4 text-slate-900 md:h-full md:pl-24 md:pr-16 md:pt-16 md:pb-10 md:[&_header]:mb-5">
      <SlideHeader unnumbered eyebrow="Appendix A · Common Questions">
        <h1 className="text-2xl font-semibold leading-tight tracking-tight md:text-4xl md:leading-[1.15]">
          Questions investors ask.
        </h1>
      </SlideHeader>

      <div className="min-h-0 md:flex-1 md:columns-2 md:gap-5">
        {QUESTIONS.map((qa) => (
          <QACard key={qa.q} {...qa} />
        ))}
      </div>
    </div>
  );
}

function QACard({ q, a }: QA) {
  return (
    <div className="mb-3.5 break-inside-avoid rounded-xl bg-white p-4 ring-1 ring-slate-200 md:mb-4">
      <p className="text-sm font-semibold leading-snug text-slate-900 md:text-[15px]">
        {q}
      </p>
      <div className="mt-2 text-xs leading-relaxed text-slate-600 md:text-[13px]">
        {a}
      </div>
    </div>
  );
}

function Tier({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p>
      <span className="font-semibold uppercase tracking-[0.1em] text-blue-700">
        {label}:
      </span>{" "}
      {children}
    </p>
  );
}
